import { encodeAudioEvent, decodeMessage, floatTo16BitPcm } from "./awsTranscribeStream";

const HANDSHAKE_TIMEOUT_MS = 8_000;
const AUDIO_RESUME_TIMEOUT_MS = 2_000;
// Time allowed for trailing final results between a graceful stop and forced teardown.
const DRAIN_MS = 3_000;
// Speech captured while presigning/handshaking is buffered and flushed once the socket
// opens. Small on purpose: a bounded backlog for AWS, and handshakes past this are dying.
const MAX_PENDING_SECONDS = 3;
// The backlog drains at this multiple of real time: fast enough to catch up while new
// audio accrues at 1x, close enough to real time for AWS's streaming buffers.
const CATCH_UP_RATE = 2;

// One live dictation session against a presigned Amazon Transcribe WebSocket. Owns the
// AudioContext/processor from beginCapture(), the socket from connect(), and — only once
// the caller grants it via takeStreamOwnership() — the mic stream, through teardown. The
// caller owns UI concerns and reacts via callbacks:
//   onTranscript(session)        — committed/provisional changed; render session.text
//   onEnded(session, reason)     — "finalized" (graceful stop completed) or "dropped"
export default class LiveDictationSession {
  constructor({ stream, onTranscript, onEnded, onOverflow }) {
    this.stream = stream;
    // The caller's fallback recorder owns the mic until this session is promoted.
    this.ownsStream = false;
    this.onTranscript = onTranscript;
    this.onEnded = onEnded;
    // Fired once if the startup buffer fills before the socket opens; the caller should
    // abandon live rather than let further speech be silently dropped.
    this.onOverflow = onOverflow;
    this.overflowed = false;
    this.committed = "";
    this.provisional = "";
    this.stopping = false;
    this.ended = false;
    this.opened = false;
    this.socket = null;
    this.audioContext = null;
    this.processor = null;
    this.drainTimer = null;
    this.handlersInstalled = false;
    this.audioReleased = false;
    this.pendingChunks = [];
    this.pendingSamples = 0;
    this.flushTimer = null;
    this.flushDone = null;
    this.sampleRate = null;
  }

  get text() {
    return `${this.committed}${this.provisional}`;
  }

  // Builds the audio graph and starts capturing immediately, so speech during the
  // presign fetch and handshake is buffered rather than lost. False = caller falls back
  // to batch on the (untouched) mic stream.
  async beginCapture() {
    try {
      this.audioContext = this.#buildAudioContext();
      const source = this.audioContext.createMediaStreamSource(this.stream);
      // 2048 samples ≈ 128ms at 16kHz, inside AWS's recommended 50-200ms chunk range.
      this.processor = this.audioContext.createScriptProcessor(2048, 1, 1);
      source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
      this.processor.onaudioprocess = (event) => this.#captureAudio(event);
      await this.#ensureAudioRunning();
      return true;
    } catch {
      this.#releaseAudio();
      return false;
    }
  }

  // Resolves true only after the AWS handshake succeeds AND the buffered startup audio
  // has fully drained — until then the caller's fallback recording stays authoritative,
  // so a stop mid-flush loses nothing. On failure the audio graph and socket are released
  // but the mic stream is left alive so the caller can fall back to batch on it.
  async connect({ url, sampleRate }) {
    this.sampleRate = sampleRate;
    try {
      this.socket = new WebSocket(url);
      this.socket.binaryType = "arraybuffer";
      await this.#waitForSocketOpen();
    } catch {
      this.ended = true;
      this.pendingChunks = [];
      this.#releaseAudio();
      this.socket?.close();
      this.socket = null;
      return false;
    }
    if (this.ended) return false;
    this.#installSocketHandlers();
    if (!(await this.#flushPending()) || this.ended) {
      this.cancel();
      return false;
    }
    return true;
  }

  // Graceful: an empty AudioEvent tells Transcribe to finalize; trailing results keep
  // flowing until the server closes the stream (bounded by the drain timer).
  stop() {
    if (this.ended || this.stopping) return;
    this.stopping = true;
    this.#releaseAudio();
    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(encodeAudioEvent(new Uint8Array(0)));
    }
    this.drainTimer = setTimeout(() => this.#finalize(), DRAIN_MS);
  }

  // Immediate teardown with no callbacks; the caller decided, so the caller updates UI.
  cancel() {
    if (this.ended) return;
    this.#teardown();
  }

  // Called when the caller promotes this session and discards its fallback recording;
  // from here on teardown stops the mic tracks.
  takeStreamOwnership() {
    this.ownsStream = true;
  }

  #captureAudio(event) {
    if (this.ended || this.stopping) return;
    const samples = event.inputBuffer.getChannelData(0);
    if (this.opened && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(encodeAudioEvent(floatTo16BitPcm(samples, this.audioContext.sampleRate, this.sampleRate)));
    } else if (this.pendingSamples < this.audioContext.sampleRate * MAX_PENDING_SECONDS) {
      // Copy: the browser reuses the underlying buffer between audioprocess events.
      this.pendingChunks.push(new Float32Array(samples));
      this.pendingSamples += samples.length;
    } else if (!this.overflowed) {
      this.overflowed = true;
      this.onOverflow?.(this);
    }
  }

  // Drain the startup backlog one chunk at a time at CATCH_UP_RATE x real time — AWS
  // expects streams close to real time, so dumping seconds of audio at once risks
  // throttling. New audio keeps buffering (in order) until fully drained.
  // Resolves false when the socket dies or the session ends before the drain completes.
  #flushPending() {
    return new Promise((resolve) => {
      this.flushDone = resolve;
      const sendNext = () => {
        if (this.ended) {
          resolve(false);
          return;
        }
        if (this.pendingChunks.length === 0) {
          this.pendingSamples = 0;
          this.opened = true;
          resolve(true);
          return;
        }
        if (this.socket.readyState !== WebSocket.OPEN) {
          this.pendingChunks = [];
          resolve(false);
          return;
        }
        const chunk = this.pendingChunks.shift();
        this.pendingSamples -= chunk.length;
        this.socket.send(encodeAudioEvent(floatTo16BitPcm(chunk, this.audioContext.sampleRate, this.sampleRate)));
        const chunkDurationMs = (chunk.length / this.audioContext.sampleRate) * 1_000;
        this.flushTimer = setTimeout(sendNext, chunkDurationMs / CATCH_UP_RATE);
      };
      sendNext();
    });
  }

  #installSocketHandlers() {
    this.handlersInstalled = true;
    this.socket.onmessage = (event) => this.#handleMessage(event.data);
    this.socket.onerror = () => {
      if (!this.ended && !this.stopping) this.#drop();
    };
    this.socket.onclose = () => {
      if (this.ended) return;
      if (this.stopping) {
        // The close we asked for: the server finalized after our end-of-stream frame.
        this.#finalize();
      } else {
        // Unexpected close (onerror is not guaranteed to fire first): an interruption,
        // not a successful stop — don't promote provisional words as if it completed.
        this.#drop();
      }
    };
  }

  #handleMessage(data) {
    if (this.ended) return;
    let message;
    try {
      message = decodeMessage(data);
    } catch {
      return;
    }
    if (message.headers[":message-type"] !== "event") {
      this.#drop();
      return;
    }
    if (message.headers[":event-type"] !== "TranscriptEvent") return;

    const results = JSON.parse(new TextDecoder().decode(message.payload))?.Transcript?.Results || [];
    for (const result of results) {
      const transcript = result.Alternatives?.[0]?.Transcript || "";
      if (result.IsPartial) {
        this.provisional = transcript;
      } else {
        this.committed = `${this.committed}${transcript} `;
        this.provisional = "";
      }
    }
    this.onTranscript(this);
  }

  #finalize() {
    if (this.ended) return;
    if (this.provisional) {
      this.committed = `${this.committed}${this.provisional}`;
      this.provisional = "";
    }
    this.#teardown();
    this.onEnded(this, "finalized");
  }

  #drop() {
    if (this.ended) return;
    this.#teardown();
    this.onEnded(this, "dropped");
  }

  #teardown() {
    this.ended = true;
    clearTimeout(this.drainTimer);
    clearTimeout(this.flushTimer);
    // A connect() awaiting the flush must settle (it resolves falsy, so connect fails).
    this.flushDone?.();
    this.pendingChunks = [];
    if (this.socket) {
      // Pre-handshake the temporary handlers must stay: closing settles (rejects) the
      // pending connect() instead of leaving it hanging until the handshake timeout.
      if (this.handlersInstalled) {
        this.socket.onmessage = null;
        this.socket.onerror = null;
        this.socket.onclose = null;
      }
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
      }
    }
    this.#releaseAudio();
    if (this.ownsStream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }
  }

  #releaseAudio() {
    if (this.audioReleased) return;
    this.audioReleased = true;
    if (this.processor) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
    }
    this.audioContext?.close();
  }

  #buildAudioContext() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    try {
      return new AudioContextClass({ sampleRate: 16_000 });
    } catch {
      return new AudioContextClass();
    }
  }

  // A suspended context never invokes ScriptProcessor callbacks — a silent failure.
  // Verify it's running (resuming if needed) so a dead context throws into batch fallback.
  async #ensureAudioRunning() {
    if (this.audioContext.state === "running") return;
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("AudioContext could not start")), AUDIO_RESUME_TIMEOUT_MS);
      Promise.resolve(this.audioContext.resume()).then(
        () => {
          clearTimeout(timeout);
          resolve();
        },
        (error) => {
          clearTimeout(timeout);
          reject(error);
        },
      );
    });
    if (this.audioContext.state !== "running") throw new Error("AudioContext could not start");
  }

  // Browsers eventually time out a dead connection, but nothing bounds it deterministically;
  // until then the mic and context would stay held. Give the handshake a hard deadline.
  #waitForSocketOpen(timeoutMs = HANDSHAKE_TIMEOUT_MS) {
    const socket = this.socket;
    if (socket.readyState === WebSocket.OPEN) return Promise.resolve();
    // Already closing/closed (e.g. it died during construction): fail now rather than
    // waiting out the timeout.
    if (socket.readyState !== WebSocket.CONNECTING) {
      return Promise.reject(new Error("WebSocket closed before opening"));
    }
    return new Promise((resolve, reject) => {
      const settle = (fn, error) => () => {
        clearTimeout(timeout);
        socket.onopen = null;
        socket.onerror = null;
        socket.onclose = null;
        fn(error);
      };
      const timeout = setTimeout(() => {
        settle(reject, new Error("WebSocket handshake timed out"))();
        socket.close();
      }, timeoutMs);
      socket.onopen = settle(resolve);
      socket.onerror = settle(reject, new Error("WebSocket handshake failed"));
      socket.onclose = settle(reject, new Error("WebSocket closed before opening"));
    });
  }
}
